import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Anchor, Trophy, Flag, MapPin, Clock, ChevronRight, Star, ArrowRight, Phone, Check, Crown, Users, Ticket, GlassWater, UtensilsCrossed, Compass, Waves } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import { CONTACT } from "../data/cruiseData";
import useTicketPrices from "../hooks/useTicketPrices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────────────────────────────────────────────────────────────────────────
   BOXING DAY CRUISE SYDNEY HARBOUR, Sydney to Hobart yacht race
   Theme: deep navy ocean + crimson racing red + cream sail-cloth accents
   Single session: 11:45am - 3:15pm, Adult $110 / Child $45
   ─────────────────────────────────────────────────────────────────────────── */

const CRIMSON = "#DC2626";
const HOT_RED = "#F87171";
const NAVY = "#0A1428";
const CREAM = "#FEF3C7";

const HIGHLIGHTS = [
 { icon: Trophy, label: "ROLEX SYDNEY-TO-HOBART", sub: "Front row start" },
 { icon: Flag, label: "1PM RACE START", sub: "Warning signal 12:50pm" },
 { icon: Anchor, label: "EXCLUSION ZONE", sub: "Best seat on the water" },
 { icon: Compass, label: "THE FLEET", sub: "Maxi yachts race past" }];

const TIMELINE = [
 { time: "11:45 AM", title: "Boarding · King St Wharf 6", body: "Step aboard Magic with cold drinks and a perfect summer harbour day ahead." },
 { time: "12:15 PM", title: "Cruise to start line", body: "Magic positions near Nielsen Park for the warning signal. Crack open your picnic basket." },
 { time: "12:50 PM", title: "Warning signal · 10 minutes", body: "The fleet locks into position. Heart-rate climbs. Bar is open for cold beers." },
 { time: "1:00 PM", title: "RACE START · GUN FIRES", body: "Maxi yachts charge past Magic in full battle mode. Goosebumps guaranteed." },
 { time: "1:30 PM", title: "Follow the fleet", body: "Cruise with the leaders as they push towards the heads. Bar service in full swing." },
 { time: "2:30 PM", title: "Fleet rounds the heads", body: "Watch the lead yachts charge out of Sydney Harbour into open ocean." },
 { time: "3:15 PM", title: "Return to wharf", body: "Cruise winds down · disembark at King St Wharf 6." }];

const PACKAGES = [
 {
  tier: "BOXING DAY · INDIVIDUAL",
  badge: "BUY TICKETS ONLINE",
  eventKey: "boxing-day-2026",
  adult: "$110",
  child: "$45",
  caption: "Per person · 3.5 hours · 11:45am, 3:15pm",
  inclusions: [
   "3.5 hour cruise", "Prime position for the Sydney to Hobart race start", "Unlimited soft drinks (included)", "BYO picnic basket welcome", "Bar snacks · available to purchase at the bar (extra)", "Beer, wine, spirits & cocktails · available to purchase at the licensed bar (extra)", "No BYO alcohol permitted", "King St Wharf 6 · boarding 11:45am"],
  cta: "Buy Boxing Day Tickets",
  highlight: true,
 },
 {
  tier: "GROUP BOOKING · 10+",
  badge: "ENQUIRE · DEPOSIT OPTION",
  adult: "$110",
  child: "$45",
  caption: "Per person · 20% deposit option",
  inclusions: [
   "All individual ticket inclusions", "20% deposit to confirm (if 21+ days out)", "Final guest count due 7 days prior", "Full payment due 21 days prior", "Credit for any reductions up to 7 days prior", "Dedicated group coordinator", "Private group section on board"],
  cta: "Enquire about group booking",
  ctaHref: "#enquiry",
  highlight: false,
 }];

const ON_BOARD_BLOCKS = [
 {
  label: "BYO picnic basket",
  badge: "INCLUDED · BRING YOUR OWN",
  badgeIncluded: true,
  Icon: UtensilsCrossed,
  caption: "Bring your own food onboard.",
  items: [
   "Pack your own picnic basket, sandwiches, salads, fruit, cheese boards, whatever you fancy", "Plates, cutlery and napkins provided", "Plenty of indoor and shaded outdoor seating", "Friends sharing? Bring extra, there's room to spread out"],
 },
 {
  label: "Bar snacks",
  badge: "AVAILABLE TO PURCHASE",
  badgeIncluded: false,
  Icon: Sparkles,
  caption: "Extra · pay at the bar.",
  items: [
   "Light bar snacks available to purchase on board", "Quick bites if you don't want to pack a full basket", "Cash and card both accepted at the bar", "Not included in your ticket price"],
 },
 {
  label: "Drinks · licensed bar",
  badge: "SOFT DRINKS INCLUDED · ALCOHOL EXTRA",
  badgeIncluded: false,
  Icon: GlassWater,
  caption: "Soft drinks free · alcohol pay-as-you-go.",
  items: [
   "Unlimited soft drinks (Coca-Cola range, juices, water), INCLUDED in your ticket", "Beer, wine, spirits and cocktails, available to PURCHASE at the licensed bar", "Cash and card both accepted at the bar", "No BYO alcohol, fully licensed venue"],
 }];

const REVIEWS = [
 { name: "Tom W.", role: "Boxing Day 2024", rating: 5, body: "Did the Sydney to Hobart start from a shore-side bar before. Never again. Watching the maxis launch from the water is something else entirely." },
 { name: "Sarah J.", role: "Boxing Day 2023", rating: 5, body: "Got the Magic for the family. Kids loved the prawns and the yachts. We didn't have to lift a finger. Perfect Boxing Day." },
 { name: "Daniel P.",role: "Boxing Day 2024", rating: 5, body: "Booked late, glad I did. Front-row to the warning gun and the start. Bar staff kept the cold ones coming. Highly recommend." }];

const FAQ = [
 { q: "What time should I arrive?", a: "Arrive 15 minutes before boarding (11:30am for 11:45am departure). King St Wharf 6, Darling Harbour. Look for the Magic crew in red shirts." },
 { q: "Where do we watch the race start?", a: "Magic anchors near Nielsen Park within sight of the start line. The 1pm gun fires across the start line and the maxis charge straight past us towards the Heads." },
 { q: "Is food included?", a: "Boxing Day is BYO picnic basket, pack your own food and bring it onboard. Bar snacks are also available to purchase on board (extra cost). Unlimited soft drinks ARE included in your ticket. Alcoholic drinks (beer, wine, spirits, cocktails) are available to purchase at the licensed bar, they are NOT included in the ticket price." },
 { q: "Are drinks at the bar free?", a: "Only soft drinks are included (unlimited Coca-Cola range, juices, water). All alcoholic drinks, beer, wine, spirits and cocktails, are pay-as-you-go at the licensed bar. Cash and card both accepted." },
 { q: "Can I BYO alcohol?", a: "No, Magic is a fully licensed venue, so no BYO alcohol is permitted. The bar serves beer, wine, spirits and cocktails. BYO food (non-alcoholic picnic) IS welcome." },
 { q: "What if the race is delayed?", a: "Boxing Day racing proceeds rain or shine, the Magic stays out to watch the entire start sequence regardless. Free cancellation up to 72hrs prior if you can't make it." },
 { q: "Is there shade?", a: "Yes, fully covered indoor area and shaded upper deck. Bring sunglasses and a light jacket, sea breeze on Sydney Harbour can be cool." }];

export default function BoxingDayCruise() {
 const [avail, setAvail] = useState(null);
 const { priceFor } = useTicketPrices({ "boxing-day-2026": { adult: 110, child: 45 } });
 const adultStr = priceFor("boxing-day-2026", "adult") || "$110";
 const childStr = priceFor("boxing-day-2026", "child") || "$45";
 const livePackages = useMemo(
  () => PACKAGES.map((p) => ({ ...p, adult: adultStr, child: childStr })),
  [adultStr, childStr],
 );
 useEffect(() => { document.title = "Boxing Day Cruise Sydney Harbour 2026 · Sydney to Hobart Race | Magic Cruises"; }, []);
 useEffect(() => {
  (async () => {
   try { const r = await axios.get(`${API}/events/boxing-day-2026/availability`); setAvail(r.data); } catch (e) { /* ignore */ }
  })();
 }, []);

 const remaining = avail?.remaining;
 const soldOut = avail?.status === "sold_out";

 return (
  <main className="bg-[#0A1428] text-white overflow-hidden" data-testid="boxing-day-page">
   <SEOHead
    title="Boxing Day Cruise Sydney Harbour 2026, Sydney to Hobart Race Tickets | Magic Cruises"
    description="Watch the Rolex Sydney to Hobart yacht race start from the water aboard Magic. 26 Dec 2026, boarding 11:45am, $110 adult / $45 child. Seafood platter, licensed bar, front-row to the start gun. King St Wharf 6."
    image="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mm8n8s47_boxing-day-cruises-sydney-harbour-magic.jpg"
    canonical="/cruises/boxing-day-cruises-sydney"
    keywords="Boxing Day cruise Sydney, Sydney to Hobart cruise tickets, Rolex Sydney Hobart cruise, race start cruise Sydney, Boxing Day boat hire Sydney"
    includeLocalBusiness
    breadcrumbs={[
     { name: "Home", url: "/" },
     { name: "Ticketed Cruises", url: "/ticketed-cruises" },
     { name: "Boxing Day Cruise Sydney Harbour", url: "/cruises/boxing-day-cruises-sydney" },
    ]}
    eventSchema={{
     name: "Boxing Day Sydney to Hobart Cruise 2026 · MV Sydney Magic",
     startDate: "2026-12-26T11:45:00+11:00",
     endDate: "2026-12-26T15:15:00+11:00",
     description: "Front-row to the Rolex Sydney to Hobart yacht race start at 1pm from the water aboard Magic. Boarding King St Wharf 11:45am, BYO picnic basket welcome, licensed bar on board.",
     offers: { price: 110, availability: "https://schema.org/InStock" },
    }}
   />

   {/* ═══════════ HERO ═══════════ */}
   <section className="relative overflow-hidden bg-[#0A1428] flex flex-col isolate" data-testid="boxing-day-hero">
    <div className="relative w-full min-h-[88vh] flex items-center justify-center px-5 sm:px-8 lg:px-10 pt-28 pb-16 lg:py-28">
     <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mm8n8s47_boxing-day-cruises-sydney-harbour-magic.jpg" alt="Boxing Day Sydney to Hobart yacht race start viewed from MV Sydney Magic on Sydney Harbour" className="absolute inset-0 w-full h-full object-cover opacity-55 scale-110" fetchPriority="high" />
     <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,20,40,0.85) 0%, rgba(14,27,54,0.55) 30%, rgba(8,17,31,0.55) 60%, rgba(8,17,31,0.95) 100%)" }} />
     <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-60" style={{ background: "radial-gradient(ellipse at center top, rgba(220,38,38,0.18) 0%, transparent 60%)" }} />
     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-60" style={{ background: "radial-gradient(ellipse at center bottom, rgba(96,165,250,0.18) 0%, transparent 65%)" }} />
     <Starfield density={45} color={CREAM} glow="rgba(254,243,199,0.45)" fixed={false} intensity={0.5} />
     <div className="absolute top-[12%] right-[8%] w-[26rem] h-[26rem] rounded-full blur-[110px] pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(220,38,38,0.22)" }} />
     <div className="absolute bottom-[8%] left-[6%] w-[22rem] h-[22rem] rounded-full blur-[100px] pointer-events-none animate-pulse" style={{ animationDuration: "8s", animationDelay: "2s", backgroundColor: "rgba(96,165,250,0.20)" }} />

     <h1 className="sr-only">Boxing Day Cruise Sydney Harbour 2026, Front-row Rolex Sydney to Hobart yacht race start aboard Magic</h1>

     <div className="absolute top-24 lg:top-28 right-5 lg:right-10 z-20 hidden lg:flex flex-col gap-2 items-end">
      {["26 Dec 2026", "King St Wharf 6", `Adult ${adultStr}`, "Front-row start"].map((b, i) => (
       <div key={b} className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-xl ring-1 text-white text-[10px] uppercase tracking-[0.28em] font-black animate-in slide-in-from-right fade-in duration-700" style={{ animationDelay: `${i * 120}ms`, borderColor: `${CRIMSON}55` }}>
        <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full" style={{ backgroundColor: CRIMSON }} />{b}</span>
       </div>
      ))}
     </div>

     <div className="relative z-10 text-center max-w-5xl mx-auto w-full">
      <div className="inline-flex items-center gap-3 sm:gap-4 mb-5 lg:mb-7 animate-in fade-in slide-in-from-top-4 duration-1000">
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: CRIMSON, opacity: 0.8 }} />
       <span className="text-white/95 text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.5em] font-bold">Rolex Sydney to Hobart · 26 Dec</span>
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: CRIMSON, opacity: 0.8 }} />
      </div>

      <h2 className="font-black tracking-tight leading-[0.88] animate-in fade-in zoom-in-95 duration-1000" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 12vw, 9rem)", letterSpacing: "-0.02em" }}>
       <span className="block bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, #FFFFFF 0%, ${CREAM} 35%, ${HOT_RED} 70%, ${CRIMSON} 100%)`, filter: "drop-shadow(0 4px 28px rgba(220,38,38,0.45))" }}>
        BOXING DAY
       </span>
       <em className="block italic font-light mt-2 lg:mt-3 text-white/90" style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.8rem)" }}>on the harbour.</em>
      </h2>

      <p className="text-white/85 text-base sm:text-lg lg:text-xl mt-6 lg:mt-7 max-w-2xl mx-auto leading-[1.65] animate-in fade-in duration-1000" style={{ animationDelay: "300ms" }}>
       Watch the Rolex Sydney to Hobart yacht race start from the best seat on the water. The fleet charges past Magic at 1pm sharp.
      </p>

      <div className="mt-9 lg:mt-12 flex flex-wrap items-center justify-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-1000" style={{ animationDelay: "500ms" }}>
       <Link to="/cruises/boxing-day-cruises-sydney/book?event=boxing-day-2026" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-white" style={{ background: `linear-gradient(135deg, ${HOT_RED} 0%, ${CRIMSON} 100%)`, boxShadow: "0 20px 50px -10px rgba(220,38,38,0.55)" }} data-testid="boxing-day-hero-cta">
        <Ticket className="w-4 h-4" /> Buy Tickets · {adultStr}
       </Link>
       <a href="#packages" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-red-300/70 hover:text-red-200 transition-all text-white" data-testid="boxing-day-hero-packages">
        Packages <ChevronRight className="w-4 h-4" />
       </a>
      </div>

      <div className="mt-10 lg:mt-14 grid grid-cols-3 gap-4 lg:gap-10 max-w-3xl mx-auto animate-in fade-in duration-1000" style={{ animationDelay: "700ms" }}>
       {[
        { i: Anchor, label: ["King St Wharf 6", "Darling Harbour"] },
        { i: Clock, label: ["3.5 Hour", "Cruise"] },
        { i: Waves, label: ["Front row", "race start"] }].map((c, i) => (
        <div key={i} className="flex flex-col items-center gap-2 lg:gap-3 text-center">
         <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ring-1 backdrop-blur" style={{ backgroundColor: "rgba(220,38,38,0.10)", borderColor: CRIMSON }}>
          <c.i className="w-4 h-4 lg:w-6 lg:h-6" style={{ color: HOT_RED }} strokeWidth={1.5} />
         </span>
         <div className="text-white text-[10px] sm:text-[11px] lg:text-sm uppercase tracking-[0.18em] lg:tracking-[0.22em] font-black leading-snug">
          {c.label.map((line, j) => <div key={j}>{line}</div>)}
         </div>
        </div>
       ))}
      </div>
     </div>
    </div>
   </section>

   {/* ═══════════ HIGHLIGHTS STRIP ═══════════ */}
   <section className="relative border-y border-red-400/15 bg-gradient-to-r from-[#0A1428] via-[#0E1B36] to-[#0A1428]" data-testid="boxing-day-highlights">
    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(220,38,38,0.10) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(96,165,250,0.08) 0%, transparent 60%)" }} />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-10">
      {HIGHLIGHTS.map((h, i) => (
       <div key={i} className="flex items-center gap-4 lg:border-r lg:border-red-400/15 lg:pr-6 last:border-0">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: "rgba(220,38,38,0.10)", borderColor: CRIMSON }}>
         <h.icon className="w-6 h-6" style={{ color: HOT_RED }} strokeWidth={1.5} />
        </div>
        <div>
         <div className="font-black text-white text-sm tracking-[0.18em]">{h.label}</div>
         <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold mt-1">{h.sub}</div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ EXPERIENCE / INTRO ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0A1428] via-[#0E1B36] to-[#0A1428] py-24 lg:py-32 overflow-hidden isolate">
    <Starfield density={32} color={CREAM} glow="rgba(254,243,199,0.4)" fixed={false} intensity={0.45} />
    <div className="absolute -top-32 left-1/3 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(220,38,38,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10 text-center">
     <CrimsonEyebrow>Australia's most famous ocean race</CrimsonEyebrow>
     <h2 className="font-black tracking-tight text-white mt-6 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
      Feel the <em className="font-light italic" style={{ color: HOT_RED }}>adrenaline</em> of the race start.
     </h2>
     <p className="text-white/82 text-base lg:text-lg leading-[1.85] mt-7 max-w-3xl mx-auto font-light">
      While some push and shove their way through the crowds on the shore to catch a glimpse of the start of the famous Sydney to Hobart, you'll be as close as it gets. The fleet of maxi yachts, super yachts and racing cruisers lines up for the warning signal at 12:50pm. The gun fires at 1:00pm. They charge past Magic in full battle mode towards the Heads. Bar is open. Prawns are out. This is the only way to do Boxing Day.
     </p>
    </div>
   </section>

   {/* ═══════════ PACKAGES ═══════════ */}
   <section id="packages" className="relative bg-gradient-to-b from-[#0A1428] via-[#0E1B36] to-[#0A1428] py-24 lg:py-32 overflow-hidden isolate border-y border-red-400/10" data-testid="boxing-day-packages">
    <Starfield density={25} color={CREAM} glow="rgba(254,243,199,0.4)" fixed={false} intensity={0.4} />
    <div className="absolute -top-32 right-1/4 w-[34rem] h-[34rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(220,38,38,0.12)" }} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <CrimsonEyebrow>Boxing Day tickets · book online</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Front-row to the <em className="italic font-light" style={{ color: HOT_RED }}>start gun.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">One session · 3.5 hours · race-start vantage point. Tickets emailed instantly.</p>
     </div>
     <div className="grid lg:grid-cols-2 gap-7 lg:gap-9">
      {livePackages.map((p, i) => (
       <div key={i} className={`relative rounded-3xl ring-1 p-8 lg:p-10 backdrop-blur-md ${p.highlight ? "lg:scale-105 z-10" : ""}`} style={{ background: p.highlight ? `linear-gradient(135deg, rgba(220,38,38,0.14), rgba(10,20,40,0.85) 60%, rgba(8,17,31,0.85))` : "rgba(14,27,54,0.55)", borderColor: p.highlight ? `${CRIMSON}66` : "rgba(255,255,255,0.12)", boxShadow: p.highlight ? "0 30px 80px -20px rgba(220,38,38,0.45)" : "0 30px 80px -20px rgba(0,0,0,0.4)" }}>
        <div className="flex items-center justify-between mb-6">
         <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-black ${p.highlight ? "" : "ring-1 ring-white/25 bg-white/10 text-white"}`} style={p.highlight ? { backgroundColor: CRIMSON, color: "#FFFFFF" } : {}}>{p.badge}</span>
         {p.highlight ? <Trophy className="w-7 h-7" style={{ color: HOT_RED }} strokeWidth={1.5} /> : <Users className="w-7 h-7 text-white/60" strokeWidth={1.5} />}
        </div>
        <div className="text-white/65 text-[11px] uppercase tracking-[0.32em] font-bold mb-2">{p.tier}</div>
        <div className="flex items-baseline gap-3 mb-1">
         <span className="text-5xl lg:text-6xl font-black text-white tabular-nums">{p.adult}</span>
         <span className="text-white/55 text-sm font-bold">adult</span>
         <span className="text-white/40 text-2xl">·</span>
         <span className="text-2xl font-black tabular-nums" style={{ color: HOT_RED }}>{p.child}</span>
         <span className="text-white/55 text-sm font-bold">child</span>
        </div>
        <div className="text-white/55 text-xs uppercase tracking-widest font-bold mb-5">{p.caption}</div>
        {p.highlight && remaining !== undefined && !soldOut && remaining <= 30 && (
         <div className="text-[11px] uppercase tracking-[0.22em] font-black mb-4 inline-flex items-center gap-1.5" style={{ color: HOT_RED }}>
          <Sparkles className="w-3 h-3" /> Only {remaining} left · selling fast
         </div>
        )}
        <ul className="space-y-3 my-6 border-t border-white/10 pt-6">
         {p.inclusions.map((inc, j) => (
          <li key={j} className="flex items-start gap-3 text-white/85 text-sm">
           <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: HOT_RED }} strokeWidth={2.5} />
           <span>{inc}</span>
          </li>
         ))}
        </ul>
        {p.highlight ? (
         soldOut ? (
          <div className="mt-2 inline-flex items-center justify-center w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase bg-red-500/15 text-red-300 ring-1 ring-red-400/30">SOLD OUT</div>
         ) : (
          <Link to={`/cruises/boxing-day-cruises-sydney/book?event=${p.eventKey}`} className="mt-2 group flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all text-white" style={{ background: `linear-gradient(135deg, ${HOT_RED} 0%, ${CRIMSON} 100%)`, boxShadow: "0 15px 40px -10px rgba(220,38,38,0.55)" }} data-testid={`boxing-day-pkg-cta-${i}`}>
           {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
         )
        ) : (
         <a href={p.ctaHref} className="mt-2 group flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all text-white ring-1 ring-white/25 hover:ring-red-300/50 hover:text-red-200" data-testid={`boxing-day-pkg-cta-${i}`}>
          {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
         </a>
        )}
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ WHAT'S ON BOARD ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#08111F] via-[#0B1830] to-[#08111F] py-24 lg:py-32 overflow-hidden isolate" data-testid="boxing-day-menu">
    <Starfield density={28} color={CREAM} glow="rgba(254,243,199,0.4)" fixed={false} intensity={0.4} />
    <div className="absolute -top-32 right-1/4 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(220,38,38,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-12">
      <CrimsonEyebrow>What's on board · BYO + bar service</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       BYO picnic, <em className="font-light italic" style={{ color: HOT_RED }}>bar service all day.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">Pack your own picnic basket and bring it onboard. Soft drinks are included, bar snacks and alcoholic drinks are <strong className="text-white">available to purchase</strong> at the licensed bar.</p>
     </div>
     {/* Clear "what's included vs what costs extra" callout */}
     <div className="grid sm:grid-cols-2 gap-3 mb-10 max-w-3xl mx-auto" data-testid="boxing-day-cost-callout">
      <div className="rounded-2xl p-5 ring-1 ring-emerald-400/30 bg-emerald-500/[0.08] flex items-start gap-3">
       <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2.5} />
       <div>
        <div className="text-emerald-300 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Included in your ticket</div>
        <div className="text-white text-sm font-bold leading-[1.55]">3.5hr cruise · Race-start vantage · Unlimited soft drinks · BYO picnic basket welcome</div>
       </div>
      </div>
      <div className="rounded-2xl p-5 ring-1 ring-amber-400/30 bg-amber-500/[0.08] flex items-start gap-3">
       <Sparkles className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
       <div>
        <div className="text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Available to purchase · extra</div>
        <div className="text-white text-sm font-bold leading-[1.55]">Bar snacks · Beer · Wine · Spirits · Cocktails (pay at the licensed bar)</div>
       </div>
      </div>
     </div>
     <div className="grid lg:grid-cols-3 gap-6">
      {ON_BOARD_BLOCKS.map((m, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4">
         <m.Icon className="w-6 h-6" style={{ color: HOT_RED }} strokeWidth={1.5} />
         <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.18em] font-black ${m.badgeIncluded ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30" : "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30"}`}>{m.badge}</span>
        </div>
        <h3 className="font-black text-white text-xl mb-1">{m.label}</h3>
        <p className="text-white/55 text-xs uppercase tracking-widest font-bold mb-5">{m.caption}</p>
        <ul className="space-y-3">
         {m.items.map((it, j) => (
          <li key={j} className="text-white/80 text-sm leading-[1.6] flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: HOT_RED }} />{it}</li>
         ))}
        </ul>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ TIMELINE ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0A1428] via-[#0E1B36] to-[#0A1428] py-24 lg:py-32 overflow-hidden isolate" data-testid="boxing-day-timeline">
    <Starfield density={45} color={CREAM} glow="rgba(254,243,199,0.45)" fixed={false} intensity={0.55} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <CrimsonEyebrow>The race-day run sheet</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       How <em className="italic font-light" style={{ color: HOT_RED }}>Boxing Day</em> on Magic unfolds.
      </h2>
     </div>
     <ol className="relative space-y-7 border-l border-red-400/25 pl-8">
      {TIMELINE.map((t, i) => (
       <li key={i} className="relative">
        <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full ring-4 ring-[#0E1B36]" style={{ backgroundColor: CRIMSON }} />
        <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: HOT_RED }}>{t.time}</div>
        <div className="text-white font-black text-lg mt-1">{t.title}</div>
        <div className="text-white/70 text-base mt-1.5 leading-[1.7]">{t.body}</div>
       </li>
      ))}
     </ol>
    </div>
   </section>

   {/* ═══════════ REVIEWS ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#08111F] via-[#0B1830] to-[#08111F] py-24 lg:py-32 border-y border-red-400/10 overflow-hidden isolate" data-testid="boxing-day-reviews">
    <Starfield density={25} color={CREAM} glow="rgba(254,243,199,0.4)" fixed={false} intensity={0.4} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <CrimsonEyebrow>Guest reviews · 4.9 / 5</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Race-day, <em className="italic font-light" style={{ color: HOT_RED }}>done right.</em>
      </h2>
     </div>
     <div className="grid md:grid-cols-3 gap-6">
      {REVIEWS.map((r, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-7">
        <div className="flex items-center gap-1 mb-4">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" style={{ color: HOT_RED }} />)}</div>
        <p className="text-white/85 text-base leading-[1.7] italic mb-5">"{r.body}"</p>
        <div className="text-white font-black text-sm">{r.name}</div>
        <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold">{r.role}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ FAQ ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#08111F] via-[#0B1830] to-[#08111F] py-24 lg:py-32 overflow-hidden isolate" data-testid="boxing-day-faq">
    <Starfield density={15} color={CREAM} glow="rgba(254,243,199,0.35)" fixed={false} intensity={0.3} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center mb-12"><CrimsonEyebrow>Frequently asked</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
       Questions, <em className="italic font-light" style={{ color: HOT_RED }}>answered.</em>
      </h2>
     </div>
     <div className="space-y-3">
      {FAQ.map((f, i) => (
       <details key={i} className="group rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-5 lg:p-6 cursor-pointer">
        <summary className="flex items-center justify-between gap-4 font-black text-white text-base lg:text-lg list-none">
         <span>{f.q}</span>
         <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-open:rotate-90" style={{ color: HOT_RED }} />
        </summary>
        <p className="text-white/72 text-base leading-[1.75] mt-4">{f.a}</p>
       </details>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ FINAL CTA ═══════════ */}
   <section id="enquiry" className="relative overflow-hidden isolate" data-testid="boxing-day-final-cta">
    <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mm8n8s47_boxing-day-cruises-sydney-harbour-magic.jpg" alt="Boxing Day Sydney to Hobart" className="absolute inset-0 w-full h-full object-cover scale-110" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1428]/95 via-[#0E1B36]/75 to-[#0E1B36]/45" />
    <Starfield density={45} color={CREAM} glow="rgba(254,243,199,0.5)" fixed={false} intensity={0.5} />
    <div className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(220,38,38,0.16)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10 py-24 lg:py-36">
     <div className="text-center">
      <CrimsonEyebrow>Boxing Day 2026 · 26 Dec</CrimsonEyebrow>
      <h2 className="font-black tracking-tight text-white mt-7 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.25rem, 5.5vw, 5rem)" }}>
       See you on <em className="italic font-light" style={{ color: HOT_RED }}>the harbour.</em>
      </h2>
      <p className="text-white/82 text-base lg:text-lg leading-[1.7] mt-6 max-w-2xl mx-auto">3.5 hours on the water · front-row to the race start · BYO picnic basket · licensed bar all cruise. Tickets emailed instantly.</p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
       <Link to="/cruises/boxing-day-cruises-sydney/book?event=boxing-day-2026" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-white" style={{ background: `linear-gradient(135deg, ${HOT_RED} 0%, ${CRIMSON} 100%)`, boxShadow: "0 20px 50px -10px rgba(220,38,38,0.6)" }} data-testid="boxing-day-final-cta">
        <Ticket className="w-4 h-4" /> Buy Boxing Day Tickets
       </Link>
       <a href={`tel:${CONTACT.phone1}`} className="group inline-flex items-center gap-2.5 text-white/85 hover:text-white px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/15 hover:border-white/40 backdrop-blur transition-all">
        <Phone className="w-4 h-4" /> {CONTACT.phone1}
       </a>
      </div>
     </div>
    </div>
   </section>

   {/* Sticky mobile CTA */}
   <div className="fixed bottom-4 left-4 right-4 lg:hidden z-40">
    <div className="rounded-2xl p-3 backdrop-blur-xl ring-1 shadow-[0_15px_40px_-10px_rgba(220,38,38,0.4)]" style={{ backgroundColor: "rgba(10,20,40,0.85)", borderColor: `${CRIMSON}55` }}>
     <Link to="/cruises/boxing-day-cruises-sydney/book?event=boxing-day-2026" className="inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-full text-white font-black text-xs tracking-[0.18em] uppercase" style={{ background: `linear-gradient(135deg, ${HOT_RED} 0%, ${CRIMSON} 100%)` }} data-testid="boxing-day-sticky-cta">
      <Ticket className="w-4 h-4" /> Buy Boxing Day Tickets · {adultStr}
     </Link>
    </div>
   </div>
  </main>
 );
}

function CrimsonEyebrow({ children }) {
 return (
  <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] font-black">
   <span className="block w-6 h-px" style={{ backgroundColor: `${CRIMSON}80` }} />
   <span style={{ color: HOT_RED }}>{children}</span>
   <span className="block w-6 h-px" style={{ backgroundColor: `${CRIMSON}80` }} />
  </div>
 );
}
